import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { siteSettingsDetals } from "../../services/siteSettingsService";

// Base color values
const colors_value = {
  primary: "#FF8927",
  secondary: "#0A192F",
  sidebar: "#172933",
  "background-light": "#EEF2F7",
  "surface-01": "#f4f4f4",
  "surface-02": "#F2F2F2",
  "surface-03": "#F6F6F6",
  "border-color": "#EBECED",
  "text-secondary": "#9A9A9A",
  "text-gradient-red": "#929292",
  "text-disabled": "#8B8A8B",
  "danger-light": "#fee2e2",
  "table-header-bg": "#0A192F",
  "table-header-text": "#ffffff",
  "table-body-bg": "#ffffff",
};

// Helper to format color values for Tailwind-style class names
function modifyValue(prefix, obj) {
  return Object.entries(obj).map(([key, value]) => ({
    [`${prefix}-${key}`]: value,
  }));
}

// Factory to generate full color set for a section (bg, border, text)
const generateColorSet = ({ key }) => ({
  hover: modifyValue(`hover:${key}`, colors_value),
  normal: modifyValue(`${key}`, colors_value),
});

// Initial state
const initialState = {
  appliedTheme: {
    colors: {
      bg: generateColorSet({ key: "bg" }),
      border: generateColorSet({ key: "border" }),
      text: generateColorSet({ key: "text" }),
    },
  },
  theme: {
    colors: {
      bg: generateColorSet({ key: "bg" }),
      border: generateColorSet({ key: "border" }),
      text: generateColorSet({ key: "text" }),
    },
  },
  colors: {
    bg: generateColorSet({ key: "bg" }),
    border: generateColorSet({ key: "border" }),
    text: generateColorSet({ key: "text" }),
  },
  selectedElement: null,
  status: "idle", // loading, ok, error
};

const dedupeColorArray = (arr) => {
  const seen = new Set();
  const result = [];

  for (const obj of arr) {
    const key = Object.keys(obj)[0];
    if (!seen.has(key)) {
      seen.add(key);
      result.push(obj);
    }
  }

  return result;
};

// Async thunk to fetch theme
export const getTheme = createAsyncThunk(
  "theme/getTheme",
  async (_, { rejectWithValue, getState }) => {
    try {
      const initials_colours_data = getState()?.themeProvider?.colors;
      const res = await siteSettingsDetals();

      const mergeAndDedupe = (initial, incoming) =>
        dedupeColorArray([...(initial || []), ...(incoming || [])]);

      const colors = {
        bg: {
          normal: mergeAndDedupe(
            res?.data?.colors?.bg?.normal,
            initials_colours_data?.bg?.normal
          ),
          hover: mergeAndDedupe(
            res?.data?.colors?.bg?.hover,
            initials_colours_data?.bg?.hover
          ),
        },
        border: {
          normal: mergeAndDedupe(
            res?.data?.colors?.border?.normal,
            initials_colours_data?.border?.normal
          ),
          hover: mergeAndDedupe(
            res?.data?.colors?.border?.hover,
            initials_colours_data?.border?.hover
          ),
        },
        text: {
          normal: mergeAndDedupe(
            res?.data?.colors?.text?.normal,
            initials_colours_data?.text?.normal
          ),
          hover: mergeAndDedupe(
            res?.data?.colors?.text?.hover,
            initials_colours_data?.text?.hover
          ),
        },
      };

      if (res?.status === 200) {
        const mod_res = {
          ...res,
          // data: { ...(res?.data?.advancedSettings || {}), colors: colors },
          data: {
            ...(res?.data || {}),
            ...(res?.data?.advancedSettings || {}),
            colors: res?.data?.advancedSettings?.colors || colors,
          },
        };
        delete mod_res?.data?.advancedSettings;
        return mod_res;
      }

      return rejectWithValue(res);
    } catch (err) {
      return rejectWithValue(err?.response?.data || err);
    }
  }
);

// Redux slice
const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    setThemeValue: (state, action) => {
      state.theme = { ...state.theme, ...action.payload };
    },
    applyTheme: (state, action) => {
      state.appliedTheme = { ...state.appliedTheme, ...action.payload };
    },
    selectElement: (state, action) => {
      state.selectedElement = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTheme.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getTheme.fulfilled, (state, action) => {
        state.status = "ok";
        const themeData = action.payload?.data || {};

        state.appliedTheme = {
          ...state.appliedTheme,
          ...themeData,
          colors: {
            ...state.appliedTheme.colors,
            ...themeData.colors,
          },
        };

        state.theme = {
          ...state.theme,
          ...themeData,
          colors: {
            ...state.theme.colors,
            ...themeData.colors,
          },
        };
      })
      .addCase(getTheme.rejected, (state) => {
        state.status = "error";
      });
  },
});

export const { setThemeValue, selectElement, applyTheme } = themeSlice.actions;
export default themeSlice.reducer;
